import { AxiosRequestConfig } from 'axios';
import { DemandResponseState } from 'enums/demandState';
import { ExtraUnitType } from 'enums/extraUnitTypes';
import { OfferState } from 'enums/offerState';
import { GetServerSidePropsContext } from 'next';
import {
  CalculatePricing,
  CreateDemandPayload,
  CreateDemandResponse,
  Demand,
  DemandGetResponse,
  DemandResponseMeal,
  DemandResponseOffer,
  DemandResponseService,
  DemandResponseVehicle,
  DemandState,
  DemandType,
  DemandVehicle,
  Extra,
  Offer,
  PaymentInfo,
  ReservationGetResponse,
  ReservationState,
  SelectedExtraUnits,
  VehicleBasedDailyWeeklyDemandState,
  VehicleBasedDemandState,
} from 'types/demand.type';
import { ExtraWithUnit } from 'types/vehicle.type';
import { client } from 'utils/axios';
import { ssrRequest } from 'utils/ssrRequestFactory';
import { dateService } from './DateService';
import {
  SupportedDailyWeeklyRentalTypes,
  SupportedHourlyRentalTypes,
} from 'enums/supportedRentalTypes';

class DemandService {
  async getUserDemandsSSR(
    ctx: GetServerSidePropsContext,
    hidePast: boolean
  ): Promise<DemandGetResponse[]> {
    return ssrRequest('/v1/customer/demand', ctx, {
      params: {
        showOld: !hidePast,
      },
    });
  }
  async getUserDemands(
    showOld: boolean,
    options: AxiosRequestConfig = {}
  ): Promise<DemandGetResponse[]> {
    const { data } = await client.get('/v1/customer/demand', {
      ...options,
      params: {
        showOld: showOld,
      },
    });
    return data;
  }

  async getUserReservationsSSR(
    ctx: GetServerSidePropsContext,
    hidePast: boolean
  ): Promise<ReservationGetResponse[]> {
    return ssrRequest('/v1/customer/reservation', ctx, {
      params: {
        showOld: !hidePast,
      },
    });
  }
  async getUserReservations(
    showOld: boolean,
    options: AxiosRequestConfig = {}
  ): Promise<ReservationGetResponse[]> {
    const { data } = await client.get('/v1/customer/reservation', {
      ...options,
      params: {
        showOld: showOld,
      },
    });
    return data;
  }

  formatVehicleBasedDemandStateExtrasWithUnitType(
    selectedServices: number[],
    selectedServicesUnits: SelectedExtraUnits,
    services: ExtraWithUnit[],
    numberOfPeople: number,
    duration: number,
    dwDurationType?: SupportedDailyWeeklyRentalTypes
  ): Record<number, number> {
    const weeklyCalculation =
      dwDurationType === SupportedDailyWeeklyRentalTypes.WEEKLY
        ? duration
        : duration / 7;
    const unitValues: Record<ExtraUnitType, number> = {
      [ExtraUnitType.HEAD_COUNT]: numberOfPeople,
      [ExtraUnitType.HOUR]: duration,
      [ExtraUnitType.HOUR_RATE]: duration,
      [ExtraUnitType.RESERVATION]: 1,
      [ExtraUnitType.DAY]:
        dwDurationType === SupportedDailyWeeklyRentalTypes.WEEKLY
          ? duration * 7
          : duration,
      [ExtraUnitType.WEEK]: Math.ceil(weeklyCalculation),
    };

    return selectedServices.reduce((acc, item) => {
      const service = services.find((s) => s.id === item);
      if (service) {
        const itemUnit =
          selectedServicesUnits[service.id] ?? unitValues[service.unit];
        return {
          ...acc,
          [item]: +itemUnit,
        };
      }
      return acc;
    }, {});
  }

  formatVehicleBasedDemandStateToDemand(
    state: VehicleBasedDemandState,
    vehicleId: number,
    supportedExtras: ExtraWithUnit[],
    paymentInfo: PaymentInfo | undefined
  ): Omit<CreateDemandPayload, 'source'> {
    const endDate = dateService.formatDateToServer(
      dateService.resetMinuteBelow(
        dateService
          .getEndDateFromHourDuration(state.date!, state.duration)
          .toDate()
      )
    );
    const selectedMeals = state.selectedMeal
      ? {
          [state.selectedMeal]: +state.numberOfPeople,
        }
      : {};
    const selectedExtras = [
      ...state.selectedServices,
      ...(state.selectedPackage ? [state.selectedPackage] : []),
    ];
    const selectedExtrasUnit = {
      ...state.selectedServicesUnits,
      ...state.selectedPackagesUnits,
    };
    const selectedServices =
      demandService.formatVehicleBasedDemandStateExtrasWithUnitType(
        selectedExtras,
        selectedExtrasUnit,
        supportedExtras,
        +state.numberOfPeople,
        +state.duration
      );

    const formattedPayload: Omit<CreateDemandPayload, 'source'> = {
      paymentInfo,
      targetVehicleId: vehicleId,
      dateTimeRange: {
        startTime: dateService.formatDateToServer(
          dateService.resetMinuteBelow(state.date!)
        ),
        endTime: endDate,
      },
      startHarborId: +state.harbor,
      finishHarborId: +state.endHarbor,
      hourlyVariant: state.isSwimmingTour
        ? SupportedHourlyRentalTypes.SWIMMING_TOUR
        : SupportedHourlyRentalTypes.HOURLY,
      passengerCount: +state.numberOfPeople,
      selectedMeals,
      selectedServices,
      platform: 'WEB',
    };
    return formattedPayload;
  }

  formatVehicleBasedDWDemandStateToDemand(
    state: VehicleBasedDailyWeeklyDemandState,
    vehicleId: number,
    supportedExtras: ExtraWithUnit[],
    paymentInfo: PaymentInfo | undefined,
    duration?: number
  ): Omit<CreateDemandPayload, 'source'> {
    const selectedExtras = [...state.selectedServices];
    const selectedExtrasUnit = {
      ...state.selectedServicesUnits,
    };
    const selectedServices =
      demandService.formatVehicleBasedDemandStateExtrasWithUnitType(
        selectedExtras,
        selectedExtrasUnit,
        supportedExtras,
        +state.numberOfPeople,
        duration || 0
      );

    return {
      paymentInfo,
      targetVehicleId: vehicleId,
      dateTimeRange: {
        startTime: dateService.formatDateToServer(
          dateService.resetMinuteBelow(state.startDate!)
        ),
        endTime: dateService.formatDateToServer(
          dateService.resetMinuteBelow(state.endDate!)
        ),
      },
      dwVariant: state.demandVariant,
      startHarborId: +state.harbor,
      finishHarborId: +state.endHarbor,
      passengerCount: +state.numberOfPeople,
      selectedMeals: {},
      selectedServices,
      platform: 'WEB',
    };
  }

  async createDemand(
    payload: CreateDemandPayload,
    options?: AxiosRequestConfig
  ): Promise<CreateDemandResponse> {
    const { data } = await client.post('/v1/customer/demand', payload, {
      hideError: true,
      ...(options || {}),
    });
    return data;
  }

  async updateDemand(
    demandId: number,
    payload: CreateDemandPayload,
    options?: AxiosRequestConfig
  ): Promise<CreateDemandResponse> {
    const { data } = await client.put(
      `/v1/customer/demand/${demandId}`,
      payload,
      {
        hideError: true,
        ...(options || {}),
      }
    );
    return data;
  }

  async calculatePricing(
    payload: CreateDemandPayload
  ): Promise<CalculatePricing.MappedResponse> {
    const { data } = await client.post<PaymentInfo>(
      '/v1/customer/demand/calculate-pricing',
      payload
    );
    const mealPrice = data.mealPricings.reduce(
      (acc, val) => acc + val.value,
      0
    );
    const extraPrice = data.servicePricings.reduce(
      (acc, val) => acc + val.value,
      0
    );
    return {
      ...data,
      mealPrice,
      extraPrice,
    };
  }

  async checkAvailableTenderVehicles(
    payload: CreateDemandPayload
  ): Promise<number> {
    const { data } = await client.post(
      `/v1/customer/demand/available-vehicles`,
      payload,
      {
        hideError: true,
      }
    );
    return data;
  }

  formatVehicle(vehicle: DemandResponseVehicle): DemandVehicle {
    return {
      id: vehicle.id,
      commentCount: vehicle.rating.vehicleAverage.commentCount,
      rating: vehicle.rating.vehicleAverage.overallAverage,
      images: vehicle.images,
      type: vehicle.type,
      vehiclePrioritizedConditions: vehicle.usageConditions.map(
        (cond) => cond.label
      ),
      brand: vehicle.brandName,
      length: vehicle.vehicleLength,
      businessModel: vehicle.businessModel,
    };
  }

  formatExtra(extra: DemandResponseService | DemandResponseMeal): Extra {
    return {
      name: extra.title,
      count: extra.count,
      price: extra?.price?.amount,
      unit: (extra as DemandResponseService)?.unit ?? ExtraUnitType.HEAD_COUNT,
    };
  }

  formatOffer(offer: DemandResponseOffer): Offer {
    const isTimeSuggested = !!offer.suggestedDateTimeRange;
    return {
      id: offer.id,
      isTimeSuggested,
      startDate: offer.suggestedDateTimeRange?.startTime
        ? dateService.formatServerDateToLocalDate(
            offer.suggestedDateTimeRange?.startTime
          )
        : null,
      endDate: offer.suggestedDateTimeRange?.endTime
        ? dateService.formatServerDateToLocalDate(
            offer.suggestedDateTimeRange?.endTime
          )
        : null,
      currency: offer.paymentInfo?.currency,
      prepaymentPrice: offer.paymentInfo?.prePaymentAmount,
      totalPrice: offer.paymentInfo?.totalAmount,
      priceToPayOnBoard: offer.paymentInfo?.priceToPayOnBoard,
      vehicle: demandService.formatVehicle(offer.vehicle),
      paymentInfo: offer.paymentInfo,
    };
  }

  formatDemand(response: DemandGetResponse): Demand {
    const demandType: DemandType = response.demand.organization
      ? DemandType.TENDER
      : DemandType.AVAILABILITY;
    const startDate = response.demand.startTime;
    const endDate = response.demand.endTime;
    const services = response.demand.services.map(demandService.formatExtra);
    const meals = response.demand.meals.map(demandService.formatExtra);
    const packages = response.demand.packages.map(demandService.formatExtra);
    const badgeState =
      response.demand.state === 'DENIED'
        ? DemandState.DENIED
        : response.demand.badgeState;

    return {
      id: response.demand.id,
      demandType,
      dwVariant: response.demand.dwVariant,
      hourlyVariant: response.demand.hourlyVariant,
      state: badgeState,
      organization: response.demand.organization,
      organizationTRName: response.demand.organizationTRName,
      numberOfPeople: response.demand.passengerCount,
      creationDate: dateService.formatServerUTCToLocal(
        response.demand.createdAt
      ),
      startDate: dateService.formatServerDateToLocalDate(startDate),
      endDate: dateService.formatServerDateToLocalDate(endDate),
      harbor: response.demand.startHarbor,
      endHarbor: response.demand.finishHarbor,
      region: response.demand.region,
      regionTRName: response.demand.regionTRName,
      ...(response.demand.targetVehicle
        ? {
            vehicle: demandService.formatVehicle(response.demand.targetVehicle),
          }
        : {}),
      extras: {
        meals,
        packages,
        services,
      },
      paymentInfo: response.demand.paymentInfo,
      offer: response.offers
        .filter((offer) => {
          const isDemandOngoing = [
            DemandResponseState.PENDING,
            DemandResponseState.PAYMENT_PENDING,
          ].includes(response.demand.state);
          if (isDemandOngoing) {
            return offer.state !== OfferState.PULLED;
          }
          return true;
        })
        .map(demandService.formatOffer),
    };
  }

  formatReservationState(state: ReservationState): DemandState {
    if (state === ReservationState.DONE) {
      return DemandState.COMPLETED;
    }
    if (state === ReservationState.CANCELLED) {
      return DemandState.CANCELLED;
    }
    return DemandState.WAITING;
  }

  formatReservation(response: ReservationGetResponse): Demand {
    const demandType: DemandType = DemandType.RESERVATION;
    const isTimeSuggested = !!response.offer.suggestedDateTimeRange;
    const startDate = isTimeSuggested
      ? response.offer.suggestedDateTimeRange.startTime
      : response.reservation.startTime;
    const endDate = isTimeSuggested
      ? response.offer.suggestedDateTimeRange.endTime
      : response.reservation.endTime;
    const services = response.reservation.services.map(
      demandService.formatExtra
    );
    const meals = response.reservation.meals.map(demandService.formatExtra);
    const packages = response.reservation.packages.map(
      demandService.formatExtra
    );
    const badgeState = demandService.formatReservationState(
      response.reservation.state
    );

    return {
      id: response.reservation.id,
      contactInfo:
        badgeState === DemandState.WAITING
          ? response.reservation.contactInfo
          : undefined,
      demandType,
      ratingDone: response.reservation.ratingDone,
      hourlyVariant: response.reservation.hourlyVariant,
      dwVariant: response.reservation.dwVariant,
      state: badgeState,
      organization: response.reservation.organization,
      organizationTRName: response.reservation.organizationTRName,
      numberOfPeople: response.reservation.passengerCount,
      creationDate: dateService.formatServerUTCToLocal(
        response.reservation.createdAt
      ),
      startDate: dateService.formatServerDateToLocalDate(startDate),
      endDate: dateService.formatServerDateToLocalDate(endDate),
      harbor: response.reservation.startHarbor,
      endHarbor: response.reservation.finishHarbor,
      region: response.reservation.region,
      regionTRName: response.reservation.regionTRName,
      extras: {
        meals,
        packages,
        services,
      },
      freeCancellable: response.reservation.freeCancellable,
      offer: [demandService.formatOffer(response.offer)],
    };
  }

  async acceptOffer(demandId: number, offerId: number) {
    const { data } = await client.post(
      `/v1/customer/demand/${demandId}/offers/${offerId}/accept`
    );
    return data;
  }

  async cancelDemand(demandId: number, cancelReason: string) {
    const { data } = await client.put(
      `/v2/customer/demand/${demandId}/delete`,
      { reason: cancelReason }
    );
    return data;
  }

  async cancelReservation(reservationId: number, cancelReason: string) {
    const { data } = await client.put(
      `/v2/customer/reservation/${reservationId}/delete`,
      { reason: cancelReason }
    );
    return data;
  }
}

export const demandService = new DemandService();
